import React, { useState, useEffect, useContext, useMemo } from "react";
import { Box, Button, Checkbox, ClickAwayListener, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Select, TextField } from "@mui/material";
import LoadingButton from '@mui/lab/LoadingButton';
import EmailTemplateBodyEditor from "components/EmailTemplateBodyEditor";

import { apiGet, apiPost } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import { Utils } from 'common/Utils';
import { PrivateRoutesContext } from "app/PrivateRoutesContext";

import moment from "moment";

import AttachEmailOutlinedIcon from '@mui/icons-material/AttachEmailOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import SendIcon from '@mui/icons-material/Send';
import AximTooltip from "components/AximTooltip";
import ConfirmDialog from "components/Content/Modal/ConfirmDialog"
import EmailAttachments from "../Table/EmailAttachments";

export default function EmailTemplateDialog({isOpen, handleClose, templateToEdit, showWelcomeEmailOption = true, allowCodeUpdate = true}) {
    const { companyId, error } = useContext(PrivateRoutesContext);

    const [isInitialized, setIsInitialized] = useState(false);
    const [templatesToCopy, setTemplatesToCopy] = useState([]);
    const [doShowTemplateToCopy, setDoShowTemplateToCopy] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [emailFroms, setEmailFroms] = useState([]);
    const [placeholders, setPlaceholders] = useState([]);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const [isCloseDisabled, setIsCloseDisabled] = useState(false);

    const [lastSavedTemplate, setLastSavedTemplate] = useState({
        templateId: Utils.emptyGuid(),
        templateDescription: '',
        templateCode: '',
        isWelcomeEmail: false,
        fromId: Utils.emptyGuid(),
        templateSubject: ''
    });

    const [currentTemplate, setCurrentTemplate] = useState({
        templateId: Utils.emptyGuid(),
        templateDescription: '',
        templateCode: '',
        isWelcomeEmail: false,
        fromId: Utils.emptyGuid(),
        templateSubject: '',
        isTestEmailRequired: false
    });

    const [savedBody, setSavedBody] = useState('');
    const [templateBody, setTemplateBody] = useState('');

    const [isSavingAttachment, setIsSavingAttachment] = useState(false);
    const [attachmentRefresh, setAttachmentRefresh] = useState(0);

    const [testEmailAddress, setTestEmailAddress] = useState('');
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);

    const [doShowConfirm, setDoShowConfirm] = useState(false);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);

    useEffect(() => {
        if (isOpen && !isInitialized) {
            apiGet(apiUrls.getGenericEmailTemplates).then(
                (response) => {
                    setTemplatesToCopy(response.data);
                }
            ).catch(
            (response) => {
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred loading templates.");
                error.setDoShowError(true);
            }
            );

            apiGet(apiUrls.getEmailAddresses).then((response) => {
                setEmailFroms(response.data);
            }).catch((response) => {
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred loading email addresses.");
                error.setDoShowError(true);
            });

            apiGet(apiUrls.getEmailTemplatePlaceholders).then((response) => {
                setPlaceholders(response.data);
                console.log(response.data);
            }).catch((response) => {
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred loading placeholders.");
                error.setDoShowError(true);
            });

            setTestEmailAddress(localStorage.getItem(`loginName`));
            setIsInitialized(true);
        }
    },[isOpen]);

    useEffect(() => {
        setSelectedTemplate('');
        setFirstEmailFrom();
        setIsTooltipVisible(false);
    },[isOpen]);

    useEffect(() => {
        if (templateToEdit) {
            setDoShowTemplateToCopy(false);
            // console.log(templateToEdit);

            setLastSavedTemplate({
                templateId: templateToEdit.emailTemplateId,
                templateDescription: templateToEdit.emailTemplateDescription,
                templateCode: templateToEdit.emailTemplateCode,
                isWelcomeEmail: templateToEdit.isWelcomeEmail,
                fromId: templateToEdit.emailFromId,
                templateSubject: templateToEdit.emailTemplateSubject
            });

            setCurrentTemplate({
                templateId: templateToEdit.emailTemplateId,
                templateDescription: templateToEdit.emailTemplateDescription,
                templateCode: templateToEdit.emailTemplateCode,
                isWelcomeEmail: templateToEdit.isWelcomeEmail,
                fromId: templateToEdit.emailFromId,
                templateSubject: templateToEdit.emailTemplateSubject,
                isTestEmailRequired: templateToEdit.isTestEmailRequired,
            });

            setSavedBody(templateToEdit.emailTemplateBody);
            setTemplateBody(templateToEdit.emailTemplateBody);
        }
        else {
            setDoShowTemplateToCopy(true);
            clearInputs();
        }
    },[templateToEdit]);

    useEffect(() => {
        setFirstEmailFrom();
    },[emailFroms])

    useEffect(() => {
        var t = templatesToCopy.find(c => c.emailTemplateId === selectedTemplate);

        if (t) {
            setCurrentTemplate({
                ...currentTemplate,
                templateId: Utils.emptyGuid(),
                templateDescription: t.emailTemplateDescription,
                templateCode: t.emailTemplateCode,
                isWelcomeEmail: t.isWelcomeEmail,
                templateSubject: t.emailTemplateSubject,
                isTestEmailRequired: t.isTestEmailRequired
            });
            
            setTemplateBody(t.emailTemplateBody);
        }
        else {
            clearInputs();
        }
        
        setAttachmentRefresh(attachmentRefresh + 1);
    }, [selectedTemplate])

    useEffect(() => {
        checkSubmitDisabled();
    },[currentTemplate,templateBody])

    const isTestEmailDisabled = useMemo(() =>
        currentTemplate.templateDescription != lastSavedTemplate.templateDescription ||
        currentTemplate.templateCode != lastSavedTemplate.templateCode ||
        currentTemplate.fromId != lastSavedTemplate.fromId ||
        currentTemplate.templateSubject != lastSavedTemplate.templateSubject ||
        (`<html><body>${templateBody}</body></html>` != savedBody && templateBody != savedBody)
    ,[currentTemplate, lastSavedTemplate, templateBody, savedBody])

    const isAttachmentDisabled = useMemo(() => currentTemplate.templateId === Utils.emptyGuid(), [currentTemplate.templateId])

    const checkSubmitDisabled = () => {
        setIsSubmitDisabled(currentTemplate.templateDescription === '' ||
            currentTemplate.templateCode === '' ||
            currentTemplate.templateSubject === '' ||
            currentTemplate.fromId === '' ||
            templateBody === '' ||
            templateBody === '<p><br></p>'); // Quill makes blank templateBody === <p><br></p>
    }

    const clearInputs = () => {
        setCurrentTemplate({
            templateId: Utils.emptyGuid(),
            templateDescription: '',
            templateCode: '',
            isWelcomeEmail: false,
            templateSubject: '',
            isTestEmailRequired: false
        });

        setLastSavedTemplate({
            templateId: Utils.emptyGuid(),
            templateDescription: '',
            templateCode: '',
            isWelcomeEmail: false,
            templateSubject: ''
        });

        setTemplateBody('');
        setSavedBody('');
    }

    const setFirstEmailFrom = () => {
        if (emailFroms.length > 0) {
            setCurrentTemplate({
                ...currentTemplate,
                fromId: emailFroms[0].emailFromId
            });
        }
    }

    const handlePromptUploadAttachment = (e) => {
        if (e.target.files[0])  {
            //console.log(e.target.files[0]);

            setConfirmTitle("Upload Attachment");
            setConfirmText(`Are you sure you would like to upload attachment: ${e.target.files[0].name}?`);
            setDoShowConfirm(true);
            setConfirmAction(() => (result) => handleUploadAttachment(e, result));
        }
    }

    const handleUploadAttachment = (e, result) => {
        if (e.target.files[0] && result)  {
            setIsSavingAttachment(true);

            const formData = new FormData();
            formData.append("file", e.target.files[0]);

            return apiPost(apiUrls.postEmailAttachment(companyId, currentTemplate.templateId), formData, {
              headers: {
                "content-type": "multipart/form-data",
                Accept: "*/*",
              },
            })
            .then ((response) => {
                setAttachmentRefresh(attachmentRefresh + 1);
            })
            .catch((response) => {
                console.log(response);
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred saving attachment.");
                error.setDoShowError(true);
            })
            .finally(() => {
                e.target.value = null;
                setIsSavingAttachment(false);
                setDoShowConfirm(false);
            });
        }
        else {
            setDoShowConfirm(false);
            e.target.value = null;
        }
    }

    const handlePromptDeleteAttachment = (cell) => {
        //console.log(cell, "cell");

        setConfirmTitle("Delete Attachment");
        setConfirmText(`Are you sure you would like to delete attachment: ${cell.fileName}?`);
        setDoShowConfirm(true);
        setConfirmAction(() => (result) => handleDeleteAttachment(cell, result));
    }

    const handleDeleteAttachment = (cell, result) => {
        setDoShowConfirm(false);

        if (result) {
            apiPost(apiUrls.deleteEmailAttachment(companyId, cell.externalEmailFileId))
            .then ((response) => {
                setAttachmentRefresh(attachmentRefresh + 1);
            })
            .catch((response) => {
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred deleting attachment.");
                error.setDoShowError(true);
            });
        }
    }

    const handleSubmit = () => {
        // TODO: Disable controls
        setIsCloseDisabled(true);
        setIsSubmitDisabled(true);

        apiPost(apiUrls.postEmailTemplate(companyId), {
            emailTemplateId: currentTemplate.templateId,
            emailTemplateDescription: currentTemplate.templateDescription,
            emailTemplateCode: currentTemplate.templateCode,
            isWelcomeEmail: currentTemplate.isWelcomeEmail,
            emailFromID: currentTemplate.fromId,
            emailTemplateSubject: currentTemplate.templateSubject,
            emailTemplateBody: templateBody
        })
            .then((response) => {
                //console.log(response);

                setIsTooltipVisible(true);
                
                setLastSavedTemplate({
                    templateId: response.data,
                    templateDescription: currentTemplate.templateDescription,
                    templateCode: currentTemplate.templateCode,
                    isWelcomeEmail: currentTemplate.isWelcomeEmail,
                    fromId: currentTemplate.fromId,
                    templateSubject: currentTemplate.templateSubject
                });
                setSavedBody(templateBody);
                setDoShowTemplateToCopy(false);
                setCurrentTemplate({ ...currentTemplate, templateId: response.data, isTestEmailRequired: true });
            })
            .catch((response) => {
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred saving Email Template.");
                error.setDoShowError(true);

                console.log(response);
            })
            .finally(() => {
                checkSubmitDisabled();
                setIsCloseDisabled(false);
            })
    }

    const handleSendTestEmail = () => {
        setIsTooltipVisible(false);
        setIsSendingTestEmail(true);

        apiPost(apiUrls.sendTestEmail(companyId, currentTemplate.templateId), { emailAddresses: [ testEmailAddress ] })
            .then((response) => {
                setCurrentTemplate({ ...currentTemplate, isTestEmailRequired: false });
            })
            .catch((response) => {
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred sending Test Email.");
                error.setDoShowError(true);
            })
            .finally((response) => {
                setIsSendingTestEmail(false);
            })
            
    }

    const handleConfirmClose = (result) => {
        confirmAction(result);
    }

    return (
        <>
            <Dialog
                open={isOpen}
                maxWidth="lg"
            >
                <DialogTitle>Email Template</DialogTitle>
                <DialogContent>
                    <Box className="container">
                        <Box className="row">
                            {doShowTemplateToCopy ? 
                                <>
                                    <Box className="col-4 text-end">
                                        <strong>Template to Copy</strong>
                                    </Box>
                                    <Box className="col-8">
                                        <Select 
                                            variant="standard"
                                            value={selectedTemplate}
                                            onChange={e => setSelectedTemplate(e.target.value)}
                                            sx={{width: "100%"}}>
                                            {templatesToCopy.map(t => <MenuItem key={t.emailTemplateId} value={t.emailTemplateId}>{t.emailTemplateDescription}</MenuItem>)}
                                        </Select>
                                    </Box>
                                </> : null}
                            <Box className="col-4 text-end">
                                <strong>Template Description</strong>
                            </Box>
                            <Box className="col-8">
                                <TextField 
                                    variant="standard"
                                    value={currentTemplate.templateDescription}
                                    onChange={e => setCurrentTemplate({ ...currentTemplate, templateDescription: e.target.value })}
                                    sx={{width: "100%"}} />
                            </Box>
                            <Box className="col-4 text-end">
                                <strong>Template Code</strong>
                            </Box>
                            <Box className="col-8">
                                <TextField
                                    variant="standard"
                                    value={currentTemplate.templateCode}
                                    onChange={e => setCurrentTemplate({ ...currentTemplate, templateCode: e.target.value })}
                                    sx={{width: "100%"}} 
                                    disabled={!allowCodeUpdate}
                                    />
                            </Box>
                            {showWelcomeEmailOption?<><Box className="col-4"></Box>
                            <Box className="col-8">
                                <FormControlLabel control={
                                    <Checkbox checked={currentTemplate.isWelcomeEmail} onChange={e => setCurrentTemplate({ ...currentTemplate, isWelcomeEmail: e.target.checked})} />
                                } label="Is Welcome Email" />
                            </Box></>:<></>}
                            
                            <Box className="col-4 text-end">
                                <strong>Email From</strong>
                            </Box>
                            <Box className="col-8">
                                <Select
                                    variant="standard"
                                    value={currentTemplate.fromId}
                                    onChange={e => setCurrentTemplate({ ...currentTemplate, fromId: e.target.value })}
                                    sx={{width: "100%"}}>
                                        {emailFroms.map(e => <MenuItem key={e.emailFromId} value={e.emailFromId}>{`${e.emailType} - ${e.email}`}</MenuItem>)}
                                </Select>
                            </Box>
                            <Box className="col-4 text-end">
                                <strong>Email Subject</strong>
                            </Box>
                            <Box className="col-8 mb-1">
                                <TextField 
                                    variant="standard"
                                    value={currentTemplate.templateSubject}
                                    onChange={e => setCurrentTemplate({ ...currentTemplate, templateSubject: e.target.value })}
                                    sx={{width: "100%"}} />
                            </Box>
                            <Box className="col-4 text-end">
                                <strong>Email Body</strong>
                            </Box>
                            <Box className="col-8">
                                <EmailTemplateBodyEditor value={templateBody}
                                    placeholders={placeholders}
                                    handleChange={e => setTemplateBody(e)} />
                            </Box>
                        </Box>
                        <Box className="row mt-2">
                            <Box className="col-4">
                                <strong>Attachments</strong>
                            </Box>
                            <Box className="col-8 text-end mb-2">
                                <AximTooltip arrow title={isAttachmentDisabled ? "Save template to enable uploading attachments" : ""}>
                                    <span>
                                        <LoadingButton
                                            variant="outlined"
                                            component="label"
                                            disabled={isAttachmentDisabled}
                                            startIcon={<AttachEmailOutlinedIcon />}
                                            loading={isSavingAttachment}
                                            loadingPosition="start"
                                        >
                                            Upload Attachment
                                            <input
                                                type="file"
                                                hidden
                                                onChange={handlePromptUploadAttachment}
                                            />
                                        </LoadingButton>
                                    </span>
                                </AximTooltip>
                            </Box>

                            <EmailAttachments
                                isEditable={true}
                                templateId={currentTemplate.templateId}
                                refresh={attachmentRefresh}
                                handlePromptDeleteAttachment={handlePromptDeleteAttachment} />
                        </Box>
                        <Box className="row mt-2">
                            <Box className="col">
                                <ClickAwayListener onClickAway={() => setIsTooltipVisible(false)}>
                                    <div>
                                        <AximTooltip
                                            arrow
                                            open={isTooltipVisible}
                                            onClose={() => setIsTooltipVisible(false)}
                                            onOpen={() => setIsTooltipVisible(true)}
                                            title={isTestEmailDisabled ? "Save Template before sending Test Email" : "Send Test Email to Enable Employee Emails"}
                                        >
                                            <span>
                                                <LoadingButton
                                                    variant="outlined"
                                                    startIcon={<SendIcon />}
                                                    className="me-2"
                                                    disabled={isTestEmailDisabled || isSendingTestEmail}
                                                    onClick={handleSendTestEmail}
                                                    loading={isSendingTestEmail}
                                                    loadingPosition="start">
                                                    Send Test
                                                </LoadingButton>
                                            </span>
                                        </AximTooltip>
                                        <TextField 
                                            variant="standard"
                                            value={testEmailAddress}
                                            onChange={e => setTestEmailAddress(e.target.value)}
                                            sx={{minWidth: "300px"}} />
                                        {currentTemplate.isTestEmailRequired ? 
                                            <AximTooltip
                                                arrow
                                                title={"Test Email is required before Employee Emails can be sent"}
                                            >
                                                <ErrorIcon color="Red" />
                                            </AximTooltip> : 
                                            null}
                                    </div>
                                </ClickAwayListener>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions>
                <Button onClick={handleSubmit} color="primary" variant="outlined" disabled={isSubmitDisabled}>
                    Save Template
                </Button>
                <Button onClick={handleClose} color="primary" variant="outlined" disabled={isCloseDisabled}>
                    Close
                </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                doShowConfirm={doShowConfirm}
                handleClose={handleConfirmClose}
                confirmTitle={confirmTitle}
                confirmText={confirmText} />
        </>
    );
}